/**
 * For You Recommendations — Shared Module
 * Identity resolution and API client for taste-personalised recommendations.
 * Used by: tags-discovery-grid (PDP tab), for-you-recommendations section (homepage),
 *          collection-for-you section (collection pages).
 */

const TASTE_SESSION_KEY = 'fy_taste_session';
const ACQ_QUIZ_KEY = 'acq-quiz-v1';
const FOR_YOU_API_PATH = '/a/accounts/for-you';

/**
 * Get market code from Shopify country (matches vespa-search.js pattern)
 */
function getMarketCode() {
  const country = window.Shopify?.country?.toLowerCase();
  if (!country) return 'gb';

  const countryToMarket = {
    'gb': 'gb', 'uk': 'gb',
    'us': 'us',
    'au': 'au',
    'ca': 'ca',
    'nz': 'nz',
    'de': 'eu', 'fr': 'eu', 'es': 'eu', 'it': 'eu', 'nl': 'eu',
    'be': 'eu', 'at': 'eu', 'pt': 'eu', 'ie': 'eu', 'fi': 'eu',
    'gr': 'eu', 'lu': 'eu', 'mt': 'eu', 'cy': 'eu', 'sk': 'eu',
    'si': 'eu', 'ee': 'eu', 'lv': 'eu', 'lt': 'eu',
  };

  return countryToMarket[country] || 'gb';
}

/**
 * Get taste identity from available sources.
 * Returns { customerId, sessionFingerprint } — at least one will be set if available.
 */
export function getTasteIdentity() {
  const result = { customerId: null, sessionFingerprint: null };

  // Logged-in customer ID from Theme global
  if (window.Theme?.customer?.loggedIn && window.Theme.customer.id) {
    result.customerId = String(window.Theme.customer.id);
  }

  // Anonymous session fingerprint from localStorage
  try {
    // Check dedicated key first (set by quiz redirect handoff)
    const stored = localStorage.getItem(TASTE_SESSION_KEY);
    if (stored) {
      result.sessionFingerprint = stored;
    }

    // Fall back to reading fingerprint from the acquisition quiz state
    if (!result.sessionFingerprint) {
      const quizState = localStorage.getItem(ACQ_QUIZ_KEY);
      if (quizState) {
        const parsed = JSON.parse(quizState);
        if (parsed.fingerprint) {
          result.sessionFingerprint = parsed.fingerprint;
        }
      }
    }
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }

  // Check URL params for quiz redirect handoff (e.g. ?fy_session=abc)
  // Persist to localStorage for future page loads
  try {
    const params = new URLSearchParams(window.location.search);
    const sessionParam = params.get('fy_session');
    if (sessionParam) {
      result.sessionFingerprint = sessionParam;
      localStorage.setItem(TASTE_SESSION_KEY, sessionParam);
      // Clean URL without reload
      const url = new URL(window.location);
      url.searchParams.delete('fy_session');
      window.history.replaceState({}, '', url);
    }
  } catch {
    // Ignore URL parsing errors
  }

  return result;
}

/**
 * Quick synchronous check: does the user have any taste identity?
 * Does NOT confirm that a TasteProfile actually exists on the server.
 */
export function hasTasteIdentity() {
  if (window.Theme?.customer?.loggedIn) return true;
  try {
    if (localStorage.getItem(TASTE_SESSION_KEY)) return true;
    const quizState = localStorage.getItem(ACQ_QUIZ_KEY);
    if (quizState) {
      const parsed = JSON.parse(quizState);
      if (parsed.fingerprint) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Check if the for-you feature flag is enabled (PostHog CSS class on <html>).
 */
export function isForYouEnabled() {
  return document.documentElement.classList.contains('for-you-enabled')
      || document.documentElement.classList.contains('style-quiz-enabled');
}

/**
 * Check if the style-quiz A/B test treatment is enabled.
 */
export function isStyleQuizEnabled() {
  return document.documentElement.classList.contains('style-quiz-enabled');
}

/**
 * Fetch personalised "For You" products from the API.
 *
 * @param {Object} [options]
 * @param {string} [options.productHandle] - Current product handle (PDP context)
 * @param {string} [options.collectionHandle] - Current collection handle
 * @param {string} [options.yqlFilter] - Raw YQL filter (from categories endpoint)
 * @param {boolean} [options.trending] - Boost popularity weight for trending grid
 * @param {string} [options.orientation] - Filter by orientation
 * @param {string[]} [options.excludeHandles] - Product handles to exclude (for cross-grid dedup)
 * @param {string[]} [options.wishlistHandles] - Top N wishlist handles to use as recommendation seeds
 * @param {number} [options.hits=16] - Number of products to return
 * @param {number} [options.offset=0] - Pagination offset
 * @returns {Promise<{products: Array, totalResults: number, hasMore: boolean, hasTasteProfile: boolean}>}
 */
export async function fetchForYouProducts(options = {}) {
  const identity = getTasteIdentity();

  if (!identity.customerId && !identity.sessionFingerprint) {
    return { products: [], totalResults: 0, hasMore: false, hasTasteProfile: false };
  }

  const {
    productHandle,
    collectionHandle,
    yqlFilter,
    trending,
    orientation,
    excludeHandles,
    wishlistHandles,
    hits = 16,
    offset = 0,
  } = options;

  const url = new URL(FOR_YOU_API_PATH, window.location.origin);
  url.searchParams.set('market', getMarketCode());

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customerId: identity.customerId || undefined,
      sessionFingerprint: identity.sessionFingerprint || undefined,
      context: {
        productHandle: productHandle || undefined,
        collectionHandle: collectionHandle || undefined,
        yqlFilter: yqlFilter || undefined,
        trending: trending || undefined,
        orientation: orientation || undefined,
        wishlistHandles: wishlistHandles?.length ? wishlistHandles : undefined,
      },
      excludeHandles: excludeHandles?.length ? excludeHandles : undefined,
      hits,
      offset,
      market: getMarketCode(),
    }),
  });

  if (!response.ok) {
    console.warn('[ForYou] API error:', response.status, response.statusText);
    return { products: [], totalResults: 0, hasMore: false, hasTasteProfile: false };
  }

  return response.json();
}

const FOR_YOU_CATEGORIES_PATH = '/a/accounts/for-you-categories';

/**
 * Fetch personalised grid categories for the For You page.
 * Returns an array of category configs derived from the user's taste profile.
 *
 * @returns {Promise<{categories: Array, hasTasteProfile: boolean}>}
 */
export async function fetchForYouCategories() {
  const identity = getTasteIdentity();

  if (!identity.customerId && !identity.sessionFingerprint) {
    return { categories: [], hasTasteProfile: false };
  }

  // Cache on window to avoid refetching on same page load
  if (window.__forYouCategories) {
    return window.__forYouCategories;
  }

  try {
    const response = await fetch(FOR_YOU_CATEGORIES_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId: identity.customerId || undefined,
        sessionFingerprint: identity.sessionFingerprint || undefined,
      }),
    });

    if (!response.ok) {
      console.warn('[ForYou] Categories API error:', response.status);
      return { categories: [], hasTasteProfile: false };
    }

    const result = await response.json();
    window.__forYouCategories = result;
    return result;
  } catch (error) {
    console.warn('[ForYou] Failed to fetch categories:', error);
    return { categories: [], hasTasteProfile: false };
  }
}
